import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppClassList } from "./AppClassList";
import { AppIcons } from "./AppIcons";
import { WhatsAppMessageId } from "../../Shared/WhatsAppMessageId";

// AppReactRules - which messages the page offers a reaction on, what a tap means, and how the
// reaction pill reads (2026-09-25, owner: "it must be possible to react to a message in WA Commander
// also"). `AppReactions` is the act; this is the rule, with no DOM, so the Bridge suite can import it.
//
// ⛔ A REACTION IS A SEND (decision 9): the owner's click on an emoji is the approval, and nothing
// here sends anything. What goes to `/send` is the message's `data-mid` and the emoji - never a key
// or a chat; the service finds those (`Bridge/src/ReactTarget.ts`).
//
// ⚠ THE PILL'S DATA IS THE SERVICE'S (`ReactionBook.DataOf`): `data-mine` is the owner's own standing
// reaction, `data-others` everyone else's (oldest first). A tap on the owner's current emoji takes it
// OFF (""), any other emoji replaces it - WhatsApp's own rule, one reaction per person per message.

/** Where the row goes, in the frame's viewport. */
export class AppReactPlace {
    public Left: number = 0;
    public Top: number = 0;
}

export class AppReactRules {
    /** ⛔ MUST EQUAL `Outbox.ReactEmoji` - pinned by `ReactContract.test.ts`. */
    public static readonly Emoji: string[] = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

    /** What each button says to a screen reader, in `Emoji`'s order. */
    public static readonly Names: string[] = ["Thumbs up", "Heart", "Laugh", "Surprised", "Sad", "Thanks"];

    /** The service's pill attributes - `ReactionBook.DataOf`. */
    public static readonly MineAttr: string = "data-mine";
    public static readonly OthersAttr: string = "data-others";
    public static readonly OthersWhoAttr: string = "data-otherswho";
    /** WhatsApp shows three distinct emoji at most (`ReactionRules.MaxEmoji`). */
    public static readonly MaxEmoji: number = 3;

    /**
     * Is this a message id the page may react to? The service's own allow-list (`Outbox.IsMessageId`).
     * DOCUMENTED EXCEPTION: pure predicate over a value in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: false IS the answer for anything that is not one.
     */
    public static IsMessageId = (Value: unknown): boolean => {
        return WhatsAppMessageId.IsValid(Value);
    };

    /**
     * Offer the row on this bubble? ⛔ ONLY WHERE A WHATSAPP ID EXISTS: a bubble with no `data-mid`
     * (or a malformed one), and the app's own just-sent bubbles (`data-wac-id`) until the rebuild
     * gives them an id, have nothing a reaction could be put on.
     */
    public static Offer = (Mid: string | null, AppendedId: string | null): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppendedId !== null && AppendedId.length > 0) { return false; }
            return AppReactRules.IsMessageId(Mid);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactRules.Offer");
            return false;
        }
    };

    /** The emoji minus its presentation selector, so a phone's bare ❤ and the row's ❤️ are one heart. */
    public static Plain = (Emoji: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return nz(Emoji).replace(/️/g, "");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactRules.Plain");
            return "";
        }
    };

    /** What a tap on `Picked` sends: "" (take it off) when it is the owner's current one, else `Picked`. */
    public static Toggle = (Current: string, Picked: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Current).length > 0 && AppReactRules.Plain(Current) === AppReactRules.Plain(Picked)) { return ""; }
            return nz(Picked);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactRules.Toggle");
            return "";
        }
    };

    /** Is `Picked` one of the row's? ⛔ The row is the allow-list - nothing else is ever sent. */
    public static Allowed = (Picked: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppReactRules.Emoji.indexOf(nz(Picked)) >= 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactRules.Allowed");
            return false;
        }
    };

    /** `data-others` -> the emoji list. */
    public static OthersOf = (Attr: string | null): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return AppClassList.Tokens(nz(Attr));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactRules.OthersOf");
            return [];
        }
    };

    /**
     * The pill's emoji and count - `ReactionBook.BadgeOf`'s rule (most used first, ties to the one
     * reacted first, three at most; the count only when more than one person). `Mine` is the newest,
     * so it goes last. Empty `Emoji` = no pill at all.
     */
    public static PillOf = (Others: string[], Mine: string): { Emoji: string[]; Count: number } => {
        if (getCoreUtils().KErrorMode === true) { return { Emoji: [], Count: 0 }; }
        try {
            const All: string[] = Others.slice();
            if (nz(Mine).length > 0) { All.push(Mine); }
            const Count: Record<string, number> = {};
            const Order: string[] = [];
            for (const One of All) {
                if (Count[One] === undefined) { Count[One] = 0; Order.push(One); }
                Count[One] = Count[One] + 1;
            }
            const Ranked: string[] = new AppReactRank(Count, Order).Sorted();
            return { Emoji: Ranked.slice(0, AppReactRules.MaxEmoji), Count: All.length };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactRules.PillOf");
            return { Emoji: [], Count: 0 };
        }
    };

    /** The pill's tooltip: "Reactions: You (heart), Anna (thumbs up)" - the owner first, as the page's own reads. */
    public static TitleOf = (Mine: string, OthersWho: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Parts: string[] = [];
            if (nz(Mine).length > 0) { Parts.push(AppIcons.WhoSaid("You", Mine)); }
            if (nz(OthersWho).length > 0) { Parts.push(nz(OthersWho)); }
            return "Reactions: " + Parts.join(", ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactRules.TitleOf");
            return "";
        }
    };

    /** The opener centre, relative to the bubble, within its visible portion below the sticky task strip. */
    public static TriggerTop = (Top: number, Bottom: number, ViewH: number, InsetTop: number): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const VisibleTop: number = Math.max(Top, InsetTop, 0);
            const VisibleBottom: number = Math.min(Bottom, ViewH);
            return Math.max(0, (VisibleTop + VisibleBottom) / 2 - Top);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactRules.TriggerTop");
            return 0;
        }
    };

    /**
     * ⛔ THE ROOM A BUBBLE'S OWN CONTROL MAY TAKE OUTSIDE ITS TOP OR BOTTOM EDGE (2026-10-13). A bubble an open task came
     * from carries the task frame's "Mark done" (`AppTaskFrame.Css`, which is written from THIS number): it hangs on the
     * top edge, or stands wholly above / below a bubble too narrow to hold it. The reaction row keeps clear of that band
     * (`PlaceRow`'s `Hang`), or the row lay over "Mark done" - the manual's own picture showed it.
     */
    public static readonly HangPx: number = 26;
    /** The attribute that says a bubble is a task's source (the page's own - `TaskStateHtml`; = `AppTaskFrame.TaskAttr`). */
    public static readonly HangAttr: string = "data-task-id";

    /**
     * Row above the bubble when there is room, else below; kept horizontally within the viewport. `Hang` = the band above
     * and below the bubble that belongs to the bubble's own control (0 for a bubble with none): the row stays outside it.
     */
    public static PlaceRow = (Left: number, Top: number, Right: number, Bottom: number, ViewW: number,
                              RowW: number, RowH: number, Mine: boolean, Hang: number = 0): AppReactPlace => {
        if (getCoreUtils().KErrorMode === true) { return new AppReactPlace(); }
        try {
            const Out: AppReactPlace = new AppReactPlace();
            Out.Left = Left;
            if (Mine === true) { Out.Left = Right - RowW; }
            Out.Left = Math.max(8, Math.min(Out.Left, ViewW - RowW - 8));
            Out.Top = Top - Hang - RowH - 6;
            if (Out.Top < 8) { Out.Top = Bottom + Hang + 6; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactRules.PlaceRow");
            return new AppReactPlace();
        }
    };

    /** What the hint under the box says when WhatsApp did not confirm - never "not sent". */
    public static readonly UnconfirmedWhy: string = "WhatsApp did not confirm that reaction - check the chat on"
        + " your phone before reacting again.";
    /** When the page stops waiting - the reaction stays queued. */
    public static readonly StillWaitingWhy: string = "Still waiting for WhatsApp to confirm the reaction. It"
        + " goes as soon as the connection allows - no need to react again.";
    /** A second tap on a message whose reaction is still on its way. */
    public static readonly BusyWhy: string = "Your last reaction on that message is still on its way - wait a moment.";
}

/** The shared tally comparator: most used first, the caller's own table for the tie. */
export class AppReactTally {
    /** Counts descending; ties go to the caller's tiebreak over its own order table. */
    public static Compare = (Count: Record<string, number>, A: string, B: string,
                             Tie: (X: string, Y: string) => number): number => {
        if (Count[A] !== Count[B]) { return Count[B] - Count[A]; }
        return Tie(A, B);
    };

    /** The same comparator with the first-seen map tie both rankers build: earliest first seen wins the tie. */
    public static CompareFirst = (Count: Record<string, number>, A: string, B: string,
                                  First: Record<string, number>): number => {
        return AppReactTally.Compare(Count, A, B, (X: string, Y: string): number => First[X] - First[Y]);
    };
}

/** Most used first, then first seen. A class, not a closure - house rule. */
class AppReactRank {
    private Count: Record<string, number>;
    private Order: string[];

    public constructor(Count: Record<string, number>, Order: string[]) {
        this.Count = Count;
        this.Order = Order;
    }

    public Sorted = (): string[] => AppReactOrder.Sorted(this.Order, this.Compare, "AppReactRank.Sorted");

    /**
     * DOCUMENTED EXCEPTION: a comparator, called once per pair inside `sort`.
     * ERROR-SUPPRESSED-JUSTIFIED: pure and total over two maps built by the caller.
     */
    private Compare = (A: string, B: string): number => {
        return AppReactTally.Compare(this.Count, A, B, (X: string, Y: string): number => this.Order.indexOf(X) - this.Order.indexOf(Y));
    };
}

/**
 * The one copy of "most used first, then the caller's own tiebreak": the copy of the order, the sort on the
 * caller's comparator, and the fault boundary around both. `AppReactRank.Sorted` (settings counts) and
 * `AppReactRanker.Sorted` (live-event counts) differ only in the comparator and the label.
 */
export class AppReactOrder {
    public static Sorted = (Order: string[], Compare: (A: string, B: string) => number, Label: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = Order.slice();
            Out.sort(Compare);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
            return [];
        }
    };
}
